import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    cancelTaskUpdateQueries,
    invalidateCloseHealthQueries,
    invalidateUpdatedTaskQueries,
    type OptimisticTaskFields,
    requestTaskUpdate,
    restoreTaskUpdateSnapshot,
    type TaskUpdateBody,
    type TaskUpdateSnapshot,
    writeOptimisticTaskUpdate,
} from "@api/task.api";
import type { Task } from "@common/types/task";
import { useToaster } from "@design-system/ToastMessages/useToaster";

/** What the board sends for a task, and what the cache shows until the response lands. */
export interface TaskBoardUpdate {
    task: Task;
    body: TaskUpdateBody;
    patch: TaskUpdateBody & OptimisticTaskFields;
}

/** An update already in the cache, with the snapshot to roll back to. */
export interface TaskBoardSaveRequest {
    task: Task;
    body: TaskUpdateBody;
    snapshot: TaskUpdateSnapshot;
    /** Toasted once the update saves, once per task and message. */
    successMessage?: string;
    /** Runs once a failed save has rolled the cache back. */
    onRevert?: () => void;
}

/**
 * Board task updates: `applyUpdate` writes one into the task's cached lists
 * at once, `save` sends it, and a failed save rolls the cache back with
 * `failureMessage`. Tasks are cached per workspace, so this works on the
 * organization page too.
 */
export const useTaskBoardSave = (failureMessage: string) => {
    const queryClient = useQueryClient();
    const toaster = useToaster();

    const { mutate: save } = useMutation({
        mutationFn: ({ task, body }: TaskBoardSaveRequest) =>
            requestTaskUpdate({ task, body }),
        onSuccess: (_data, { task, successMessage }) => {
            if (successMessage !== undefined) {
                toaster.toast(successMessage, `${task.id}:${successMessage}`);
            }
        },
        onError: (_error, { snapshot, onRevert }) => {
            restoreTaskUpdateSnapshot(queryClient, snapshot);
            onRevert?.();
            toaster.error(failureMessage);
        },
        onSettled: (_data, _error, { task, body }) => {
            invalidateUpdatedTaskQueries({
                queryClient,
                workspaceKey: task.workspaceId,
                taskId: task.id,
            });
            invalidateCloseHealthQueries({ queryClient, task, body });
        },
    });

    const applyUpdate = useCallback(
        ({ task, body, patch }: TaskBoardUpdate): TaskBoardSaveRequest => {
            const cacheArgs = { queryClient, workspaceKey: task.workspaceId };
            void cancelTaskUpdateQueries({ ...cacheArgs, taskId: task.id });
            return {
                task,
                body,
                snapshot: writeOptimisticTaskUpdate({
                    ...cacheArgs,
                    task,
                    payload: patch,
                }),
            };
        },
        [queryClient],
    );

    const revert = useCallback(
        ({ snapshot }: TaskBoardSaveRequest) =>
            restoreTaskUpdateSnapshot(queryClient, snapshot),
        [queryClient],
    );

    return { applyUpdate, save, revert };
};
